import { useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowDown, faLocationDot, faMobileScreen, faRobot, faEarthAsia, faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { faReact, faAngular, faNodeJs } from '@fortawesome/free-brands-svg-icons';

import Myself from '../../assets/images/myself.jpeg';
import resumePdf from '../../assets/Darshan_Lal_CV.pdf';
import { siteConfig } from '../../config/site';
import portfolio from '../../data';
import { decimalYearsSince, CAREER_START } from '../../utils/yearsOfExperience';
import useMagnetic from '../../hooks/useMagnetic';
import { prefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import CountUp from '../ui/CountUp';
import Typewriter from './Typewriter';

import '../../styles/Hero/Hero.scss';

const ROLES = [
  'Frontend Developer',
  'Backend Developer',
  'Forward Deployed Engineer',
  'Full Stack Engineer',
  'React Native Developer',
  'LLM & AI Engineer',
];

const OPEN_TO = ['Frontend Developer', 'Backend Developer', 'Full Stack', 'Forward Deployed Engineer'];

const CORE_STACK = [
  { label: 'React', color: '#61dafb' },
  { label: 'Angular', color: '#ff4f6d' },
  { label: 'React Native', color: '#7dd3fc' },
  { label: 'Next.js', color: '#ffffff' },
  { label: 'Node.js', color: '#83cd29' },
];

const HEADLINE = [
  { text: 'Building' },
  { text: 'software' },
  { text: 'that', br: true },
  { text: 'takes', accent: true },
  { text: 'flight.', accent: true },
];

const ORBIT = [
  { icon: faReact, label: 'React', color: '#61dafb' },
  { icon: faAngular, label: 'Angular', color: '#dd0031' },
  { icon: faNodeJs, label: 'Node.js', color: '#83cd29' },
  { icon: faMobileScreen, label: 'React Native', color: '#7dd3fc' },
  { icon: faRobot, label: 'AI / LLMs', color: '#a3e635' },
];

const Hero = () => {
  const years = Math.floor(decimalYearsSince(CAREER_START) * 10) / 10;
  const projectCount = portfolio.portfolio.projectCard.cards.length;
  const primaryRef = useMagnetic(0.25);
  const ghostRef = useMagnetic(0.25);
  const portraitRef = useRef(null);

  const onPortraitMove = (e) => {
    const el = portraitRef.current;
    if (!el || prefersReducedMotion()) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--rx', `${-y * 14}deg`);
    el.style.setProperty('--ry', `${x * 14}deg`);
  };
  const onPortraitLeave = () => {
    const el = portraitRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  let wordIndex = 0;

  return (
    <section className="hero" id="top" aria-labelledby="hero-title">
      <div className="container hero__container">
        <div className="hero__content">
          <p className="hero__badge hero__anim" style={{ '--d': 0 }}>
            <span className="hero__pulse" aria-hidden="true" />
            Available immediately
            <span className="hero__badge-loc">
              <span className="hero__badge-sep" aria-hidden="true" />
              <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /> Dubai, UAE
            </span>
            <span className="hero__badge-reloc">
              <span className="hero__badge-sep" aria-hidden="true" />
              <FontAwesomeIcon icon={faEarthAsia} aria-hidden="true" /> Open to relocation
            </span>
          </p>

          <p className="hero__hello hero__anim" style={{ '--d': 1 }}>
            Hi, I&apos;m <strong>{siteConfig.displayName}</strong>{' '}
            <span className="hero__wave" aria-hidden="true">
              👋
            </span>
          </p>

          <h1 className="hero__title" id="hero-title">
            {HEADLINE.map((w) => {
              wordIndex += 1;
              return (
                <span key={w.text}>
                  <span className="hero__word-mask">
                    <span
                      className={`hero__word${w.accent ? ' gradient-text' : ''}`}
                      style={{ '--w': wordIndex }}
                    >
                      {w.text}
                    </span>
                  </span>{' '}
                  {w.br && <br className="hero__br" />}
                </span>
              );
            })}
          </h1>

          <p className="hero__role hero__anim" style={{ '--d': 6 }}>
            <span className="hero__role-prefix">&gt;</span> <Typewriter words={ROLES} />
          </p>

          <p className="hero__description hero__anim" style={{ '--d': 7 }}>
            Software Engineer focused on <span>React, Angular, React Native, Next.js and Node.js</span>. I've built
            airport departure-control and baggage systems at <span>flydubai</span> and logistics and e-commerce platforms
            used by thousands of businesses. I also build LLM features with OpenAI, LangChain and LangGraph, and I learn
            fast, so I'm productive in any stack.
          </p>

          <ul className="hero__core hero__anim" style={{ '--d': 8 }} aria-label="Core stack">
            {CORE_STACK.map((t) => (
              <li key={t.label} style={{ '--c': t.color }}>
                <span aria-hidden="true" />
                {t.label}
              </li>
            ))}
            <li className="hero__core-more">+ any stack, fast</li>
          </ul>

          <p className="hero__open hero__anim" style={{ '--d': 8 }}>
            <span className="hero__open-label">
              <FontAwesomeIcon icon={faBriefcase} aria-hidden="true" /> Open to roles
            </span>
            {OPEN_TO.map((r) => (
              <span key={r} className="hero__open-role">
                {r}
              </span>
            ))}
          </p>

          <div className="hero__cta hero__anim" style={{ '--d': 8 }}>
            <a ref={primaryRef} className="btn btn--primary" href="#work">
              View my work <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <a ref={ghostRef} className="btn btn--ghost" href={resumePdf} download={siteConfig.resumeDownloadFilename}>
              Download résumé <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
            </a>
          </div>

          <dl className="hero__stats hero__anim" style={{ '--d': 9 }}>
            <div>
              <dt>Years experience</dt>
              <dd>
                <CountUp end={years} decimals={1} suffix="+" />
              </dd>
            </div>
            <div>
              <dt>Products shipped</dt>
              <dd>
                <CountUp end={projectCount} suffix="+" />
              </dd>
            </div>
            <div>
              <dt>Businesses on Shipox</dt>
              <dd>
                <CountUp end={60} suffix="k+" />
              </dd>
            </div>
            <div>
              <dt>Passengers / min boarding</dt>
              <dd>
                <CountUp end={40} suffix="+" />
              </dd>
            </div>
          </dl>
        </div>

        <div className="hero__visual hero__anim" style={{ '--d': 3 }}>
          <div
            className="hero__portrait"
            ref={portraitRef}
            onMouseMove={onPortraitMove}
            onMouseLeave={onPortraitLeave}
          >
            <div className="hero__ring" aria-hidden="true" />
            <div className="hero__photo">
              <img src={Myself} alt={`Portrait of ${siteConfig.displayName}`} width="420" height="420" />
            </div>

            <ul className="hero__orbit" aria-hidden="true">
              {ORBIT.map((item, i) => (
                <li key={item.label} style={{ '--i': i, '--n': ORBIT.length, '--c': item.color }}>
                  <span>
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                </li>
              ))}
            </ul>

            <div className="hero__float hero__float--top">
              <span className="hero__float-dot" aria-hidden="true" />
              <div>
                <strong>Aviation systems</strong>
                <small>flydubai · DCS &amp; BMS</small>
              </div>
            </div>
            <div className="hero__float hero__float--bottom">
              <span className="hero__float-emoji" aria-hidden="true">
                🤖
              </span>
              <div>
                <strong>LLM apps</strong>
                <small>OpenAI · LangChain · LangGraph</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <a href="#about" className="hero__scroll" aria-label="Scroll to About section">
        <span className="hero__scroll-mouse" aria-hidden="true">
          <span />
        </span>
        <span className="hero__scroll-text">Scroll</span>
      </a>
    </section>
  );
};

export default Hero;
